/**
 * useMonitoringParams
 *
 * Provides a unified interface for reading and writing all deep-linkable
 * monitoring UI state via URL search parameters.
 *
 * URL parameter reference:
 *
 *   mv    – monitoring view: overview | dispatch | forecast | history | gcp_forecasts
 *   dm    – display mode:    power | energy
 *   fv    – flexibility chart view: interval | cumulative | both
 *   hc    – history card index:   0 | 1
 *   htr   – history time range:   24h | 7d | 30d | month:<YYYY-MM> | week:<YYYY-Www>
 *   hmix  – FlexibilityBandChart baseline blend 0–100
 *   hnorm – FlexibilityBandChart normalize cumulative: 1 = on
 *   sth   – SoCJourneyScatter threshold (integer)
 *   set   – SoCJourneyScatter early-unplug tolerance in minutes (integer)
 *   sho   – SoCJourneyScatter hidden outcomes (comma-separated)
 *   shd   – SoCJourneyScatter hidden duration buckets (comma-separated)
 *   sht   – SoCJourneyScatter hidden unplug timings (comma-separated)
 *   shf   – SoCJourneyScatter hidden feasibility values (comma-separated)
 *   sid   – SoCJourneyScatter selected session ID (opens detail dialog)
 */

import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { TimeRange } from './use-monitoring'

// ─── Types ─────────────────────────────────────────────────────────────────────

export type MonitoringView = 'overview' | 'dispatch' | 'forecast' | 'history' | 'gcp_forecasts'
export type DisplayMode = 'power' | 'energy'
export type FlexibilityChartView = 'interval' | 'cumulative' | 'both'

export interface MonitoringParams {
  // MonitoringDashboard
  view: MonitoringView
  displayMode: DisplayMode

  // HistoryView
  historyCardIndex: number
  historyTimeRange: TimeRange

  // FlexibilityBandChart
  flexibilityChartView: FlexibilityChartView
  forecastMidpointMix: number
  forecastNormalizeCumulative: boolean

  // SoCJourneyScatter
  scatterThreshold: number
  scatterEarlyUnplugToleranceMin: number
  scatterHiddenOutcomes: Set<string>
  scatterHiddenDurations: Set<string>
  scatterHiddenTimings: Set<string>
  scatterHiddenFeasibilities: Set<string>
  scatterSelectedSessionId: string | null

}

export interface MonitoringParamSetters {
  setView: (v: MonitoringView) => void
  setDisplayMode: (m: DisplayMode) => void
  setHistoryCardIndex: (i: number) => void
  setHistoryTimeRange: (r: TimeRange) => void
  setFlexibilityChartView: (v: FlexibilityChartView) => void
  setForecastMidpointMix: (v: number) => void
  setForecastNormalizeCumulative: (v: boolean) => void
  setScatterThreshold: (v: number) => void
  setScatterEarlyUnplugToleranceMin: (v: number) => void
  setScatterHiddenOutcomes: (s: Set<string>) => void
  setScatterHiddenDurations: (s: Set<string>) => void
  setScatterHiddenTimings: (s: Set<string>) => void
  setScatterHiddenFeasibilities: (s: Set<string>) => void
  setScatterSelectedSessionId: (id: string | null) => void
}

// ─── Default values ────────────────────────────────────────────────────────────
//
// These must match the defaults used by each component so that the initial state
// without any URL params renders identically to the old behaviour.

const DEFAULTS = {
  view: 'overview' as MonitoringView,
  displayMode: 'power' as DisplayMode,
  historyCardIndex: 0,
  historyTimeRange: '24h' as TimeRange,
  flexibilityChartView: 'interval' as FlexibilityChartView,
  forecastMidpointMix: 0,
  forecastNormalizeCumulative: false,
  scatterThreshold: 2,
  scatterEarlyUnplugToleranceMin: 30,
  scatterHiddenOutcomes: new Set<string>(),
  scatterHiddenDurations: new Set<string>(),
  // Matches initial state in SoCJourneyScatter: early timings hidden by default
  scatterHiddenTimings: new Set<string>(['early']),
  // Matches initial state in SoCJourneyScatter: infeasible + unknown hidden by default
  scatterHiddenFeasibilities: new Set<string>(['infeasible', 'unknown']),
  scatterSelectedSessionId: null as string | null,
}

// ─── TimeRange serialisation ───────────────────────────────────────────────────

function serializeTimeRange(tr: TimeRange): string {
  if (typeof tr === 'string') return tr
  if (tr.type === 'month') return `month:${tr.month}`
  if (tr.type === 'week') return `week:${tr.isoWeek}`
  return `day:${tr.date}`
}

function deserializeTimeRange(raw: string | null): TimeRange {
  if (!raw) return DEFAULTS.historyTimeRange
  if (raw === '24h' || raw === '7d' || raw === '30d') return raw
  if (raw.startsWith('month:')) return { type: 'month', month: raw.slice(6) }
  if (raw.startsWith('week:')) return { type: 'week', isoWeek: raw.slice(5) }
  if (raw.startsWith('day:')) return { type: 'day', date: raw.slice(4) }
  return DEFAULTS.historyTimeRange
}

// ─── Set serialisation (comma-separated, omit if equals default) ───────────────

function serializeSet(s: Set<string>): string {
  return [...s].sort().join(',')
}

function deserializeSet(raw: string | null): Set<string> {
  if (!raw) return new Set()
  return new Set(raw.split(',').filter(Boolean))
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getInt(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key)
  if (raw === null) return fallback
  const n = parseInt(raw, 10)
  return isNaN(n) ? fallback : n
}

function setParamOrDelete(
  params: URLSearchParams,
  key: string,
  value: string,
  defaultValue: string
) {
  if (value === defaultValue) {
    params.delete(key)
  } else {
    params.set(key, value)
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useMonitoringParams(): MonitoringParams & MonitoringParamSetters {
  const [searchParams, setSearchParams] = useSearchParams()

  // ── Read ───────────────────────────────────────────────────────────────────

  const rawView = searchParams.get('mv') ?? ''
  const view: MonitoringView =
    rawView === 'overview' ||
    rawView === 'dispatch' ||
    rawView === 'forecast' ||
    rawView === 'history' ||
    rawView === 'gcp_forecasts'
      ? rawView
      : DEFAULTS.view

  const rawDm = searchParams.get('dm') ?? ''
  const displayMode: DisplayMode =
    rawDm === 'power' || rawDm === 'energy' ? rawDm : DEFAULTS.displayMode

  const historyCardIndex = getInt(searchParams, 'hc', DEFAULTS.historyCardIndex)
  const historyTimeRange = deserializeTimeRange(searchParams.get('htr'))

  const rawFv = searchParams.get('fv') ?? ''
  const flexibilityChartView: FlexibilityChartView =
    rawFv === 'interval' || rawFv === 'cumulative' || rawFv === 'both'
      ? rawFv
      : DEFAULTS.flexibilityChartView

  const forecastMidpointMix = getInt(searchParams, 'hmix', DEFAULTS.forecastMidpointMix)
  const forecastNormalizeCumulative = searchParams.get('hnorm') === '1'

  const scatterThreshold = getInt(searchParams, 'sth', DEFAULTS.scatterThreshold)
  const scatterEarlyUnplugToleranceMin = getInt(
    searchParams,
    'set',
    DEFAULTS.scatterEarlyUnplugToleranceMin
  )

  const rawSho = searchParams.get('sho')
  const scatterHiddenOutcomes =
    rawSho !== null ? deserializeSet(rawSho) : DEFAULTS.scatterHiddenOutcomes

  const rawShd = searchParams.get('shd')
  const scatterHiddenDurations =
    rawShd !== null ? deserializeSet(rawShd) : DEFAULTS.scatterHiddenDurations

  const rawSht = searchParams.get('sht')
  const scatterHiddenTimings =
    rawSht !== null ? deserializeSet(rawSht) : DEFAULTS.scatterHiddenTimings

  const rawShf = searchParams.get('shf')
  const scatterHiddenFeasibilities =
    rawShf !== null ? deserializeSet(rawShf) : DEFAULTS.scatterHiddenFeasibilities

  const scatterSelectedSessionId = searchParams.get('sid')

  // ── Write ──────────────────────────────────────────────────────────────────
  //
  // Each setter patches only its own key(s), preserving all other params.
  // Values equal to the default are deleted to keep URLs short.

  const setView = useCallback(
    (v: MonitoringView) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'mv', v, DEFAULTS.view)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setDisplayMode = useCallback(
    (m: DisplayMode) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'dm', m, DEFAULTS.displayMode)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setHistoryCardIndex = useCallback(
    (i: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'hc', String(i), String(DEFAULTS.historyCardIndex))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setHistoryTimeRange = useCallback(
    (r: TimeRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(
            next,
            'htr',
            serializeTimeRange(r),
            serializeTimeRange(DEFAULTS.historyTimeRange)
          )
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setFlexibilityChartView = useCallback(
    (v: FlexibilityChartView) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'fv', v, DEFAULTS.flexibilityChartView)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setForecastMidpointMix = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'hmix', String(v), String(DEFAULTS.forecastMidpointMix))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setForecastNormalizeCumulative = useCallback(
    (v: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (v) {
            next.set('hnorm', '1')
          } else {
            next.delete('hnorm')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterThreshold = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'sth', String(v), String(DEFAULTS.scatterThreshold))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterEarlyUnplugToleranceMin = useCallback(
    (v: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          setParamOrDelete(next, 'set', String(v), String(DEFAULTS.scatterEarlyUnplugToleranceMin))
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterHiddenOutcomes = useCallback(
    (s: Set<string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const serialized = serializeSet(s)
          const defaultSerialized = serializeSet(DEFAULTS.scatterHiddenOutcomes)
          setParamOrDelete(next, 'sho', serialized, defaultSerialized)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterHiddenDurations = useCallback(
    (s: Set<string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const serialized = serializeSet(s)
          const defaultSerialized = serializeSet(DEFAULTS.scatterHiddenDurations)
          setParamOrDelete(next, 'shd', serialized, defaultSerialized)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterHiddenTimings = useCallback(
    (s: Set<string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const serialized = serializeSet(s)
          const defaultSerialized = serializeSet(DEFAULTS.scatterHiddenTimings)
          setParamOrDelete(next, 'sht', serialized, defaultSerialized)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterHiddenFeasibilities = useCallback(
    (s: Set<string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const serialized = serializeSet(s)
          const defaultSerialized = serializeSet(DEFAULTS.scatterHiddenFeasibilities)
          setParamOrDelete(next, 'shf', serialized, defaultSerialized)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setScatterSelectedSessionId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id) {
            next.set('sid', id)
          } else {
            next.delete('sid')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return {
    // values
    view,
    displayMode,
    historyCardIndex,
    historyTimeRange,
    flexibilityChartView,
    forecastMidpointMix,
    forecastNormalizeCumulative,
    scatterThreshold,
    scatterEarlyUnplugToleranceMin,
    scatterHiddenOutcomes,
    scatterHiddenDurations,
    scatterHiddenTimings,
    scatterHiddenFeasibilities,
    scatterSelectedSessionId,
    // setters
    setView,
    setDisplayMode,
    setHistoryCardIndex,
    setHistoryTimeRange,
    setFlexibilityChartView,
    setForecastMidpointMix,
    setForecastNormalizeCumulative,
    setScatterThreshold,
    setScatterEarlyUnplugToleranceMin,
    setScatterHiddenOutcomes,
    setScatterHiddenDurations,
    setScatterHiddenTimings,
    setScatterHiddenFeasibilities,
    setScatterSelectedSessionId,
  }
}
